import { FC, useState } from 'react';
import { useHistory } from 'react-router';
import { Flex } from '@chakra-ui/react';

import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import ErrorView from 'components/ErrorView';
import { logEvent } from 'features/Analytics/analytics';
import TrackView from 'features/Analytics/TrackView';
import * as ContentLayout from 'layouts/ContentLayout';

import NavigationHeader from '../../components/NavigationHeader';
import {
  useBankTransferContext,
  useBankTransferStepContext,
} from '../../contexts';
import { navigateToBankTransfer } from '../../helpers';
import routes from '../../routes';
import { CreateNewBeneficiary } from '../CreateNewBeneficiary';
import BeneficiariesList, {
  BeneficiariesListProps,
} from './components/BeneficiariesList';
import useBeneficiaries from './hooks/useBeneficiaries';
import locales from './locales';

type BeneficiariesProps = Omit<BeneficiariesListProps, 'onBeneficiaryClick'>;

const BeneficiariesOutsideBankTransferContext: FC<BeneficiariesProps> = (
  props,
) => {
  const history = useHistory();

  const handleBeneficiaryClick = (bankTransferRecipientId: string) => {
    navigateToBankTransfer(history, {
      bankTransfer: {
        bankTransferRecipientId,
        transferType: 'external',
      },
      variant: 'toBeneficiary',
    });
  };

  return (
    <ContentLayout.Root>
      <TrackView pageName="Beneficiaries" />
      <ContentLayout.Body>
        <NavigationHeader />
        <Flex align="center" justify="space-between" marginBottom="space-32">
          <Heading size="lg">{locales.beneficiaries}</Heading>
          <Button
            icon="user-plus"
            iconPosition="left"
            onClick={() => {
              logEvent({ name: 'Click_Add_Beneficiary' });
              history.push({
                pathname: routes.newBeneficiary,
              });
            }}
          >
            {locales.addBeneficiary}
          </Button>
        </Flex>
        <BeneficiariesList
          {...props}
          onBeneficiaryClick={handleBeneficiaryClick}
        />
      </ContentLayout.Body>
    </ContentLayout.Root>
  );
};

const BeneficiariesInsideBankTransferContext: FC<BeneficiariesProps> = (
  props,
) => {
  const { setBankTransfer } = useBankTransferContext('selectBeneficiary');
  const { goBack, goNext } = useBankTransferStepContext();
  const [shouldRenderNewBeneficiaryStep, setShouldRenderNewBeneficiaryStep] =
    useState(false);

  const handleBeneficiaryClick = (bankTransferRecipientId: string) => {
    setBankTransfer((prev) => ({
      ...prev,
      bankTransferRecipientId,
    }));
    goNext({ key: 'setExternalDetails' });
  };

  const handleCreateBeneficiaryClick = () => {
    logEvent({ name: 'Click_Add_Beneficiary' });
    setShouldRenderNewBeneficiaryStep(true);
  };

  if (shouldRenderNewBeneficiaryStep) {
    return (
      <CreateNewBeneficiary
        allowTransferToCreatedBeneficiary
        onBackClick={() => setShouldRenderNewBeneficiaryStep(false)}
        onTransferToCreatedBeneficiary={handleBeneficiaryClick}
      />
    );
  }

  return (
    <ContentLayout.Root>
      <TrackView pageName="Beneficiaries" />
      <ContentLayout.Body>
        <NavigationHeader onBack={goBack} />

        <Flex align="center" justify="space-between" marginBottom="space-32">
          <Heading size="lg">{locales.beneficiary}</Heading>
          <Button
            icon="user-plus"
            iconPosition="left"
            onClick={handleCreateBeneficiaryClick}
          >
            {locales.addBeneficiary}
          </Button>
        </Flex>

        <BeneficiariesList
          {...props}
          onBeneficiaryClick={handleBeneficiaryClick}
        />
      </ContentLayout.Body>
    </ContentLayout.Root>
  );
};

interface BeneficiariesContainerProps {
  isCreatingNewTransfer?: boolean;
}

const BeneficiariesContainer: FC<BeneficiariesContainerProps> = ({
  isCreatingNewTransfer,
}) => {
  const { beneficiaries, error, ...useBeneficiariesQueryResult } =
    useBeneficiaries();

  if (error && beneficiaries.length === 0) {
    return <ErrorView />;
  }

  return (
    <>
      <TrackView pageName="Beneficiaries" />
      {isCreatingNewTransfer ? (
        <BeneficiariesInsideBankTransferContext
          beneficiaries={beneficiaries}
          {...useBeneficiariesQueryResult}
        />
      ) : (
        <BeneficiariesOutsideBankTransferContext
          beneficiaries={beneficiaries}
          {...useBeneficiariesQueryResult}
        />
      )}
    </>
  );
};

export default BeneficiariesContainer;
